import { useState, useEffect } from 'react';
import { getUsers, createUser, deleteUser } from '../../services/userService';
import { UserPlus, Trash2, Users, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import './UserManagement.css';

const UserManagement = ({ onShowToast }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'member'
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const result = await getUsers();
      if (result.success) {
        setUsers(result.users);
      } else {
        onShowToast(result.message || 'Failed to fetch users', 'error');
      }
    } catch (error) {
      onShowToast('Unable to connect to server', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }
    
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const result = await createUser(formData.username, formData.password, formData.role);
      
      if (result.success) {
        onShowToast('Team member created successfully', 'success');
        setFormData({ username: '', password: '', role: 'member' });
        setShowForm(false);
        fetchUsers();
      } else {
        onShowToast(result.message || 'Failed to create user', 'error');
      }
    } catch (error) {
      onShowToast('Unable to connect to server', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`Delete user "${username}"? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      const result = await deleteUser(userId);
      
      if (result.success) {
        onShowToast('User deleted successfully', 'success');
        fetchUsers();
      } else {
        onShowToast(result.message || 'Failed to delete user', 'error');
      }
    } catch (error) {
      onShowToast('Unable to connect to server', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="user-management">
      <div className="user-management-header">
        <div className="header-left">
          <Users size={24} />
          <h2>Team Members</h2>
        </div>
        <button 
          className="btn-add-user"
          onClick={() => setShowForm(!showForm)}
          disabled={loading}
        >
          <UserPlus size={18} />
          <span>Add Member</span>
        </button>
      </div>

      {showForm && (
        <div className="user-form-card">
          <h3>Create New Team Member</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="username">Username *</label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Enter username"
                  disabled={loading}
                />
                {errors.username && (
                  <span className="error-text">
                    <AlertCircle size={14} />
                    {errors.username}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  id="password"
                  name="password"
                  type="text"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter password"
                  disabled={loading}
                />
                {errors.password && (
                  <span className="error-text">
                    <AlertCircle size={14} />
                    {errors.password}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="role">Role</label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn-cancel"
                onClick={() => {
                  setShowForm(false);
                  setFormData({ username: '', password: '', role: 'member' });
                  setErrors({});
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn-submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="spin" size={16} />
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    <span>Create User</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="users-list">
        {loading && users.length === 0 ? (
          <div className="loading-state">
            <Loader2 className="spin" size={32} />
            <p>Loading team members...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <Users size={48} />
            <p>No team members yet</p>
            <span>Create your first team member to get started</span>
          </div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Role</th>
                <th>Created</th>
                <th>Created By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user._id}>
                  <td className="username-cell">{user.username}</td>
                  <td>
                    <span className={`role-badge ${user.role}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>{user.createdBy || '-'}</td>
                  <td>
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(user._id, user.username)}
                      disabled={loading}
                      title="Delete user"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
